/* src/components/sessions/AllSessionsPage.js */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../../contexts/ProfileContext'; // useProfile provides fetchAllSessionsForExport now
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faList,
  faCalendarAlt,
  faArrowLeft,
  faInfoCircle,
  // faSearch, // <-- Removed faSearch as it's not used in the component code provided
  faFilter,
  faFilterCircleXmark // <-- Added for clear filter button
} from '@fortawesome/free-solid-svg-icons';

// Component imports
import LoadingSpinner from '../ui/LoadingSpinner';
import PaginationControls from '../ui/PaginationControls';
import AllSessionsTable from './AllSessionsTable';
import DateFilterControls from './DateFilterControls';
import ExportButton from './ExportButton'; // Will be modified next

const ITEMS_PER_PAGE_OPTIONS = [25, 50, 100];

const AllSessionsPage = () => {
    const navigate = useNavigate();
    // Get fetchAllSessionsForExport from the context
    const { activeProfile, fetchPaginatedSessions, fetchAllSessionsForExport } = useProfile();
    const profileId = activeProfile?.id;

    // State for data (keep as is - this holds the *paginated* data for display)
    const [sessions, setSessions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for pagination (keep as is)
    const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
    const [currentPageNum, setCurrentPageNum] = useState(1);
    const [pageSnapshots, setPageSnapshots] = useState({ 1: null });
    const [totalFilteredSessions, setTotalFilteredSessions] = useState(0);

    // State for filtering (keep as is)
    const [startDate, setStartDate] = useState(null); // These Date objects will be passed to ExportButton
    const [endDate, setEndDate] = useState(null);   // These Date objects will be passed to ExportButton
    const [triggerFetch, setTriggerFetch] = useState(0);
    const [filtersActive, setFiltersActive] = useState(false);

    // Calculate total pages (keep as is)
    const totalPages = useMemo(() => {
        return Math.max(1, Math.ceil(totalFilteredSessions / pageSize));
    }, [totalFilteredSessions, pageSize]);

    // --- Fetching Logic (for pagination - keep as is) ---
    useEffect(() => {
        if (!profileId) {
            setIsLoading(false);
            setError("No active profile selected.");
            setSessions([]);
            setTotalFilteredSessions(0);
            return;
        }

        const fetchData = async () => {
            setIsLoading(true);
            setError(null);

            const lastVisibleDocId = pageSnapshots[currentPageNum] || null;

            // --- START CHANGE: Adjust End Date ---
            let adjustedEndDateISO = null;
            if (endDate) {
                // Create a new Date object based on the selected endDate
                const endOfDayDate = new Date(endDate);
                // Set the time to the very end of that day *in the local timezone*
                endOfDayDate.setHours(23, 59, 59, 999);
                // Convert this end-of-day local time to UTC ISO string for the backend
                adjustedEndDateISO = endOfDayDate.toISOString();
                // console.log(`Original End Date: ${endDate.toISOString()}, Adjusted End Date for Query: ${adjustedEndDateISO}`); // Keep commented out for production
            }
            // --- END CHANGE ---

            const options = {
                pageSize,
                lastVisibleDocId,
                startDate: startDate ? startDate.toISOString() : null,
                // Use the adjusted end date ISO string
                endDate: adjustedEndDateISO,
            };

            // console.log("Fetching sessions with options:", options); // Log options for debugging - Keep commented out for production
            const result = await fetchPaginatedSessions(profileId, options);

            if (result.success) {
                // console.log(`Fetched ${result.sessions.length} sessions. Total Filtered: ${result.totalFilteredSessions}`); // Log success - Keep commented out for production
                setSessions(result.sessions);
                setTotalFilteredSessions(result.totalFilteredSessions);

                // Store the snapshot for the next page
                if (result.lastVisibleDocId) {
                     setPageSnapshots(prev => ({
                         ...prev,
                         [currentPageNum + 1]: result.lastVisibleDocId
                     }));
                }

                // Reset snapshots beyond the current page if fewer items are returned
                if (result.sessions.length < pageSize || !result.lastVisibleDocId) {
                    // console.log("Detected end of results or less than page size, clearing future page snapshots."); // Keep commented out for production
                    setPageSnapshots(prev => {
                        const newSnapshots = {};
                        for (const page in prev) {
                            // Keep snapshots up to and including the *next* potential page (current + 1)
                            // But only if we *have* a snapshot for it (meaning a lastVisibleDocId was returned)
                            if (parseInt(page, 10) <= currentPageNum || (parseInt(page, 10) === currentPageNum + 1 && result.lastVisibleDocId)) {
                                newSnapshots[page] = prev[page];
                            }
                        }
                        return newSnapshots;
                    });
                }

            } else {
                console.error("Failed to load sessions:", result.error); // Log error - Keep for production debugging
                setError(result.error || "Failed to load sessions.");
                setSessions([]);
                setTotalFilteredSessions(0);
            }
            setIsLoading(false);
        };

        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileId, currentPageNum, pageSize, triggerFetch, fetchPaginatedSessions, startDate, endDate]); // Added startDate, endDate as dependencies to ensure refetch on change

    // Track whether filters are active (keep as is)
    useEffect(() => {
        setFiltersActive(!!(startDate || endDate));
    }, [startDate, endDate]);

    // --- Handlers (keep as is) ---
    const handlePageChange = useCallback((newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
             // Ensure we have the snapshot for the target page before changing page number
             if (newPage > currentPageNum && !pageSnapshots[newPage]) {
                 // This condition shouldn't ideally happen if snapshot clearing logic is correct,
                 // but as a fallback, log it. We don't re-trigger fetch here, rely on snapshot clearing logic.
                 console.warn(`Attempting to go to page ${newPage} without snapshot.`);
                 // Don't proceed if snapshot isn't ready for the next page.
                 // User should wait for current fetch to complete which populates the next snapshot.
                 if (isLoading) return; // Prevent changing page while loading
             }
              // Check if going back and snapshot exists or going forward and snapshot exists
             if ((newPage < currentPageNum && pageSnapshots[newPage] !== undefined) || // Going back (snapshot for target page must exist, null is valid for page 1)
                 (newPage > currentPageNum && pageSnapshots[newPage]) || // Going forward (snapshot must exist and be non-null)
                 (newPage === 1 && pageSnapshots[1] === null)) // Going to page 1 explicitly
             {
                // console.log(`Changing page from ${currentPageNum} to ${newPage}`); // Keep commented out for production
                 setCurrentPageNum(newPage);
             } else {
                  console.warn(`Page change blocked: Target Page: ${newPage}, Current Page: ${currentPageNum}, Snapshot Exists: ${!!pageSnapshots[newPage]}`);
             }
        } else {
             console.warn(`Attempted to navigate to invalid page: ${newPage} / ${totalPages}`);
        }
    }, [totalPages, pageSnapshots, currentPageNum, isLoading]); // Added isLoading dependency

    const handlePageSizeChange = useCallback((newPageSize) => {
        // console.log(`Changing page size to: ${newPageSize}`); // Keep commented out for production
        setPageSize(newPageSize);
        setCurrentPageNum(1); // Reset to first page
        setPageSnapshots({ 1: null }); // Clear snapshots
        setTriggerFetch(c => c + 1); // Trigger refetch
    }, []);

    const handleDateFilterChange = useCallback((newStartDate, newEndDate) => {
        // Check if dates actually changed to avoid unnecessary refetch
         const startChanged = (newStartDate?.getTime() || null) !== (startDate?.getTime() || null);
         const endChanged = (newEndDate?.getTime() || null) !== (endDate?.getTime() || null);

         if (startChanged || endChanged) {
             // console.log(`Date filters changed: Start: ${newStartDate}, End: ${newEndDate}`); // Keep commented out for production
             setStartDate(newStartDate);
             setEndDate(newEndDate);
             setCurrentPageNum(1); // Reset to first page
             setPageSnapshots({ 1: null }); // Clear snapshots
             setTriggerFetch(c => c + 1); // Trigger refetch
        } else {
             // console.log("Date filters unchanged, skipping refetch."); // Keep commented out for production
        }
    }, [startDate, endDate]);

    const handleClearAllFilters = useCallback(() => {
        if (startDate || endDate) {
            // console.log("Clearing all date filters."); // Keep commented out for production
            setStartDate(null);
            setEndDate(null);
            setCurrentPageNum(1);
            setPageSnapshots({ 1: null });
            setTriggerFetch(c => c + 1);
        }
    }, [startDate, endDate]);

    // --- Display Logic ---
    const displayTotal = totalFilteredSessions > 0
        ? `${totalFilteredSessions} session${totalFilteredSessions !== 1 ? 's' : ''}`
        : (filtersActive ? '0 sessions found' : `All ${totalFilteredSessions} sessions`); // Use filtersActive flag

    return (
        <div className="max-w-7xl mx-auto p-4 sm:p-6 bg-gray-100 min-h-screen">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-8">
                <div className="text-gray-800 mb-4 sm:mb-0"> {/* Added bottom margin for mobile */}
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2 flex items-center"> {/* Reduced bottom margin */}
                        <FontAwesomeIcon icon={faList} className="mr-3 h-6 w-6 text-indigo-600" />
                        Session History
                    </h1>
                    <p className="text-gray-600">View and analyze your complete training history</p>
                </div>
                <button
                    onClick={() => navigate('/dashboard')}
                    className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded px-1 self-start sm:self-center" // Align left on mobile
                >
                    ← Back to Dashboard
                </button>
            </div>

            {/* Filter & Actions Section */}
            <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200 mb-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-200 pb-4 mb-4">
                    <h2 className="text-lg font-semibold text-gray-800 flex items-center mb-3 sm:mb-0">
                        <FontAwesomeIcon icon={faFilter} className="mr-2 text-gray-600" />
                        Filters
                    </h2>

                    {filtersActive && (
                        <button
                            onClick={handleClearAllFilters}
                            className="text-sm text-red-600 hover:text-red-800 bg-red-50 px-3 py-1 rounded-md flex items-center border border-red-200 hover:bg-red-100 transition-colors self-start sm:self-center" // Align left on mobile
                        >
                            <FontAwesomeIcon icon={faFilterCircleXmark} className="mr-1.5" /> {/* Use specific clear icon */}
                            Clear Date Filters
                        </button>
                    )}
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    {/* Date Filter Controls */}
                    <DateFilterControls
                        onFilterChange={handleDateFilterChange}
                        initialStartDate={startDate}
                        initialEndDate={endDate}
                    />

                    {/* Summary & Export */}
                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto mt-4 sm:mt-0"> {/* Added margin top for mobile */}
                        <div className="flex items-center justify-center px-3 py-2 bg-gray-100 rounded-md border border-gray-200 text-gray-700 text-sm w-full sm:w-auto whitespace-nowrap"> {/* Ensure no wrap */}
                            <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-gray-500" />
                            <span>{isLoading ? 'Loading...' : displayTotal}</span>
                        </div>

                        {/* --- START: MODIFIED ExportButton Props --- */}
                        <ExportButton
                            // REMOVED: data={sessions} // No longer using paginated data for export
                            profileId={profileId} // Pass the active profile ID
                            startDate={startDate} // Pass the start date state (Date object or null)
                            endDate={endDate}     // Pass the end date state (Date object or null)
                            fetchAllSessionsForExport={fetchAllSessionsForExport} // Pass the fetch action from context
                            filenamePrefix={`sessions_${activeProfile?.username || 'user'}`}
                            // Disable based on profile existence or if main page data is loading
                            disabled={!profileId || isLoading}
                            className="w-full sm:w-auto" // Ensure button takes full width on mobile if needed
                        />
                         {/* --- END: MODIFIED ExportButton Props --- */}
                    </div>
                </div>
            </div>

            {/* Data Display Section */}
            {isLoading ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                    <LoadingSpinner message="Loading sessions..." />
                </div>
            ) : error ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                    <div className="text-center p-4 bg-red-50 rounded border border-red-200 text-red-600">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                        {error}
                    </div>
                </div>
            ) : sessions.length > 0 ? ( // Only show table/pagination if paginated sessions exist for display
                 <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8 overflow-hidden"> {/* Added overflow hidden */}
                     {/* Table still uses the paginated 'sessions' state for display */}
                     <AllSessionsTable sessions={sessions} />

                     {totalPages > 1 && (
                         <div className="p-4 border-t border-gray-200">
                             <PaginationControls
                                 currentPage={currentPageNum}
                                 totalPages={totalPages}
                                 onPageChange={handlePageChange}
                                 itemsPerPage={pageSize}
                                 onItemsPerPageChange={handlePageSizeChange}
                                 totalItems={totalFilteredSessions}
                                 itemsPerPageOptions={ITEMS_PER_PAGE_OPTIONS}
                             />
                         </div>
                     )}
                 </div>
            ) : (
                // Empty State - Moved outside the conditional rendering for loading/error
                 <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                        <div className="h-20 w-20 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 mb-4">
                            <FontAwesomeIcon icon={faCalendarAlt} className="h-8 w-8" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-700 mb-1">No Sessions Found</h3>
                        <p className="text-gray-500 max-w-md mb-6">
                            {filtersActive
                                ? "No sessions match your current filter criteria. Try adjusting your filters or clearing them completely."
                                : "You haven't completed any training sessions yet. Start training to build your history."}
                        </p>

                        {filtersActive ? (
                            <button
                                onClick={handleClearAllFilters}
                                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                <FontAwesomeIcon icon={faFilterCircleXmark} className="mr-2" /> {/* Use specific clear icon */}
                                Clear Date Filters
                            </button>
                        ) : (
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                                Back to Dashboard
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AllSessionsPage;